/* ---------------------------------------------------------
   Universal Countdown Timer for Shopify
   
   Usage:
   startCountdown({
     date: '2025-11-28',        // YYYY-MM-DD format
     time: '00:00',             // HH:MM in 24-hour format (LA timezone)
     timezone: 'America/Los_Angeles',
     element: '.countdown-timer'
   });
--------------------------------------------------------- */

function startCountdown(config) {
  const { date, time, timezone = 'America/Los_Angeles', element } = config;
  
  const wrapper = document.querySelector(element);
  if (!wrapper) {
    console.error(`Element "${element}" not found`);
    return;
  }

  // Calculate target timestamp in LA timezone
  const targetTimestamp = getTargetTimestamp(date, time, timezone);
  
  if (targetTimestamp < Date.now()) {
    wrapper.dispatchEvent(
      new CustomEvent('countdown:finished', { bubbles: true })
    );
    wrapper.style.display = 'none';
    return;
  }

  // Build countdown HTML
  wrapper.innerHTML = `
    <div class="common-timer-box days-box"><span class="days">00</span> DAYS</div>
    <span class="sep sep-hours">:</span>

    <div class="common-timer-box hours-box"><span class="hours">00</span> HRS</div>
    <span class="sep sep-min">:</span>

    <div class="common-timer-box minutes-box"><span class="minutes">00</span> MIN</div>
    <span class="sep sep-sec">:</span>

    <div class="common-timer-box seconds-box"><span class="seconds">00</span> SEC</div>
  `;

  // Register with countdown manager
  CountdownManager.register(targetTimestamp, {
    wrapper,
    days: wrapper.querySelector('.days'),
    hours: wrapper.querySelector('.hours'),
    minutes: wrapper.querySelector('.minutes'),
    seconds: wrapper.querySelector('.seconds'),
    daysBox: wrapper.querySelector('.days-box'),
    hoursBox: wrapper.querySelector('.hours-box'),
    minutesBox: wrapper.querySelector('.minutes-box'),
    secondsBox: wrapper.querySelector('.seconds-box'),
    sepHours: wrapper.querySelector('.sep-hours'),
    sepMin: wrapper.querySelector('.sep-min'),
    sepSec: wrapper.querySelector('.sep-sec')
  });
}

/* ---------------------------------------------------------
   Convert date + time in specific timezone to UTC timestamp
--------------------------------------------------------- */
function getTargetTimestamp(dateStr, timeStr, tz) {
  // Parse date and time
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);
  
  // Create date string in ISO format
  const dateTimeStr = `${dateStr}T${timeStr}:00`;
  
  // Method: Create a date and check what time it shows in target timezone
  // Then adjust until we get the correct local time
  
  // Start with UTC time
  let utcTime = Date.UTC(year, month - 1, day, hours, minutes, 0);
  
  // Check what this UTC time shows in the target timezone
  let testDate = new Date(utcTime);
  let tzTime = testDate.toLocaleString('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  // Parse the result to get offset
  const tzParts = tzTime.match(/(\d+)\/(\d+)\/(\d+),\s(\d+):(\d+):(\d+)/);
  const tzMonth = parseInt(tzParts[1]);
  const tzDay = parseInt(tzParts[2]);
  const tzYear = parseInt(tzParts[3]);
  const tzHour = parseInt(tzParts[4]);
  const tzMinute = parseInt(tzParts[5]);
  
  // Calculate offset in milliseconds
  const targetLocal = Date.UTC(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute, 0);
  const offset = utcTime - targetLocal;
  
  // Apply offset to get correct UTC time for target local time
  const correctUtcTime = Date.UTC(year, month - 1, day, hours, minutes, 0) + offset;
  
  return correctUtcTime;
}

/* ---------------------------------------------------------
   Countdown Manager - Handles multiple timers
--------------------------------------------------------- */
const CountdownManager = {
  timers: [],
  intervalId: null,

  register(targetTimestamp, elements) {
    const timer = {
      target: targetTimestamp,
      finished: false,
      ...elements
    };

    this.timers.push(timer);
    this.updateTimer(timer);
    this.start();
  },

  start() {
    if (this.intervalId) return;
    this.intervalId = setInterval(() => {
      this.timers.forEach(t => this.updateTimer(t));
    }, 1000);
  },

  updateTimer(timer) {
    const remaining = timer.target - Date.now();

    if (remaining <= 0) {
      this.finishTimer(timer);
      return;
    }

    const { days, hours, minutes, seconds } = this.calculateTime(remaining);

    // Update display
    timer.days.textContent = days;
    timer.hours.textContent = hours;
    timer.minutes.textContent = minutes;
    timer.seconds.textContent = seconds;

    // Show/hide units based on time remaining
    if (parseInt(days) > 0) {
      this.showDaysMode(timer);
    } else {
      this.showHoursMode(timer);
    }

    // Add urgency class for last minute
    if (remaining < 60000) {
      timer.wrapper.classList.add('countdown--urgent');
    } else {
      timer.wrapper.classList.remove('countdown--urgent');
    }
  },

  finishTimer(timer) {
    if (!timer.finished) {
      timer.finished = true;
      timer.wrapper.style.display = 'none';
      timer.wrapper.dispatchEvent(
        new CustomEvent('countdown:finished', { bubbles: true })
      );
    }
  },


  calculateTime(ms) {
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);

    /*
  return {
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00'
  };
*/

    
    return {
      days: String(days).padStart(2, '0'),
      hours: String(hours).padStart(2, '0'),
      minutes: String(minutes).padStart(2, '0'),
      seconds: String(seconds).padStart(2, '0')
    };
  },

  showDaysMode(timer) {
    timer.daysBox.style.display = 'flex';
    timer.hoursBox.style.display = 'flex';
    timer.minutesBox.style.display = 'flex';
    timer.secondsBox.style.display = 'none';
    
    timer.sepHours.style.display = 'inline';
    timer.sepMin.style.display = 'inline';
    timer.sepSec.style.display = 'none';
  },

  showHoursMode(timer) {
    timer.daysBox.style.display = 'none';
    timer.hoursBox.style.display = 'flex';
    timer.minutesBox.style.display = 'flex';
    timer.secondsBox.style.display = 'flex';
    
    timer.sepHours.style.display = 'none';
    timer.sepMin.style.display = 'inline';
    timer.sepSec.style.display = 'inline';
  }
};

/* ---------------------------------------------------------
   EXAMPLES - How to use on your Shopify store
--------------------------------------------------------- */

// Example 1: Black Friday Sale (Nov 28, 2025 midnight LA time)
// startCountdown({
//   date: '2025-11-28',
//   time: '00:00',
//   timezone: 'America/Los_Angeles',
//   element: '.black-friday-countdown'
// });

// Example 2: Cyber Monday Sale (Dec 1, 2025 midnight LA time)
// startCountdown({
//   date: '2025-12-01',
//   time: '00:00',
//   timezone: 'America/Los_Angeles',
//   element: '.cyber-monday-countdown'
// });

// Example 3: Flash Sale (Today at 6 PM LA time)
// const today = new Date().toISOString().split('T')[0];
// startCountdown({
//   date: today,
//   time: '18:00',
//   timezone: 'America/Los_Angeles',
//   element: '.flash-sale-countdown'
// });